import { nanoid } from "@reduxjs/toolkit";
import {
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import LanguagesForm from "../../../common/components/functional/languagesForm/LanguagesForm";
import { Language } from "../../talents/talentAPI";

type Props = {
  languageId?: string;
  onClose: () => void;
};

const Languages = ({ languageId, onClose }: Props) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const languages: Language[] = getValues("languages");
  const selectedLanguage = languages.find((lang) => lang.id === languageId);
  const existingLanguageCodes = languages.map((lang) => lang.languageCode);

  const defaultValues = {
    languageCode: selectedLanguage?.languageCode || "",
    skillLevel: selectedLanguage?.skillLevel || "",
  };

  const methods = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const onSave = (language: FieldValues) => {
    const existingLanguage = languages.find(
      (lang) => lang.languageCode === language.languageCode,
    );

    if (existingLanguage) {
      const updatedLanguage = {
        ...existingLanguage,
        skillLevel: language.skillLevel,
      };

      const updatedLanguages = languages.map((lang) =>
        lang.languageCode === language.languageCode ? updatedLanguage : lang,
      );
      setValue("languages", updatedLanguages, { shouldDirty: true });
      return onClose();
    }

    const updatedLanguages = [...languages, { ...language, id: nanoid() }];
    setValue("languages", updatedLanguages, { shouldDirty: true });
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <DialogContentWrapper
        title={t("languageSkill:language")}
        margin
        onSave={methods.handleSubmit(onSave)}
        onCancel={onClose}
      >
        <LanguagesForm
          disabledLanguageCodes={existingLanguageCodes}
          disableLanguageSelect={!!selectedLanguage}
        />
      </DialogContentWrapper>
    </FormProvider>
  );
};

export default Languages;
